import React, { useEffect } from 'react'
import { useField } from 'formik'
import { countries } from '@sketch/constants'
import { Form, SelectDropdown, SelectDropdownItem } from '@sketch/components'

const countriesKeys = Object.keys(countries) as (keyof typeof countries)[]

interface CountryFieldProps {
  name: string
  disabled?: boolean
  fetchBrowserLang?: boolean
}

const CountryField: React.FC<CountryFieldProps> = ({
  name,
  disabled,
  fetchBrowserLang,
}) => {
  const [field, meta, { setValue }] = useField<(typeof countriesKeys)[number]>({
    name,
  })

  // try to assert which country the user is in based on their browser language
  useEffect(() => {
    const getCountryCodeFromLanguage = () => {
      if (!fetchBrowserLang) {
        return
      }

      const language = navigator.language
      const countryCode = language.split('-')[1] || 'US' // Default to 'US' if no country code is found
      setValue(countryCode.toUpperCase() as (typeof countriesKeys)[number])
    }

    getCountryCodeFromLanguage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Form.Field
      id="country"
      label="Country"
      errorText={meta.touched ? meta.error : undefined}
    >
      <SelectDropdown
        autoComplete="country"
        value={field.value}
        name={field.name}
        placeholder="Select a country"
        disabled={disabled}
        items={countriesKeys}
        onChange={country => setValue(country)}
        renderValueText={country => countries[country]}
        renderItem={(country, { setItem, a11yProps }) => (
          <SelectDropdownItem
            active={a11yProps['aria-selected']}
            text={countries[country]}
            onClick={() => setItem(country)}
            {...a11yProps}
          />
        )}
      />
    </Form.Field>
  )
}

export default CountryField
